'use client';

import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import type { FileUploadFile } from './file-upload';
import { useFileUploadContext, validateFiles } from './file-upload';

const dropzoneVariants = cva(
  'relative flex flex-col items-center justify-center rounded-lg transition-colors ' +
    'data-drag-over:border-primary data-drag-over:bg-primary/5 ' +
    'data-disabled:pointer-events-none data-disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-2 border-dashed border-border bg-muted/30 hover:border-primary/60',
        solid: 'border border-border bg-muted/30 hover:border-primary/60',
        ghost: 'border-none bg-transparent',
      },
      size: {
        sm: 'min-h-24 p-4 text-xs',
        default: 'min-h-36 p-6 text-sm',
        lg: 'min-h-48 p-8 text-base',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

type FileUploadDropzoneProps = React.ComponentProps<'div'> & VariantProps<typeof dropzoneVariants>;

// gloss
const FileUploadDropzone = ({
  className,
  variant = 'default',
  size = 'default',
  children,
  ...props
}: FileUploadDropzoneProps) => {
  const { files, onFilesChange, accept, maxSize, maxFiles, multiple, disabled, onValidationError } =
    useFileUploadContext();
  const [isDragOver, setIsDragOver] = React.useState(false);

  const handleDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (disabled) return;

      const incoming = Array.from(e.dataTransfer.files);
      if (!incoming.length) return;

      const { valid, errors } = validateFiles(incoming, multiple ? files : [], { accept, maxSize, maxFiles, multiple });

      if (errors.length) onValidationError?.(errors);

      if (valid.length) {
        const newFiles: FileUploadFile[] = valid.map((file) => ({
          id: crypto.randomUUID(),
          file,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        }));

        if (multiple) {
          onFilesChange([...files, ...newFiles]);
        } else {
          onFilesChange(newFiles.slice(0, 1));
        }
      }
    },
    [files, onFilesChange, accept, maxSize, maxFiles, multiple, disabled, onValidationError],
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target — keyboard interaction handled by nested FileUploadTrigger button
    <div
      data-slot="file-upload-dropzone"
      data-drag-over={isDragOver || undefined}
      data-disabled={disabled || undefined}
      className={cn(dropzoneVariants({ variant, size, className }))}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      {...props}
    >
      {children}
    </div>
  );
};

export { dropzoneVariants, FileUploadDropzone };
